import {
    panel,
    PropertiesPanel,
    PropertiesPanelAxisToggle,
    PropertiesPanelLeafConfig,
    PropertiesPanelPickerOption,
    PropertiesPanelBulletsProvider,
    PropertiesPanelSection,
    PropertiesPanelTabs
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { Spinner } from '../../../../../widgets/spinner';
import { SearchInput } from '../../../../../widgets/input';
import { useDialogManager } from '../../../../../widgets/dialog';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useMaterialsPropertiesCreator } from './materialsPropertiesConfig';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import InspectorShell, { InspectorMetaAction, InspectorUsage } from './inspectorShell';
import { CamerasIcon, ObjectsIcon } from '../../../../../widgets/icons/scene';
import { showErrorNotification, showSuccessNotification } from '../../../../../widgets/notificationToaster';
import { captureMaterialPreview, materialSwatchFallback, readMaterialPreview, saveMaterialPreview } from './materialPreview';
import { sceneAssetUrl } from './sceneUrls';
import { buildPbrMaterialPanel, filterPbrMaterialPanel, PbrPanelSection } from './buildPbrMaterialPanel';
import { textureMapping } from './textureMapping';
import { hasSurface } from './objectEditorConfig';
import { notifyMaterialUsageChanged } from './materialUsage';
import { buildPbrExtraProperties } from './pbrExtraProperties';
import { FinishMode } from './pbrMaterialLayout';

const generateTextureUniqueId = <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
    return [material.id, type, mobile ? 'mobile' : 'default'].join('_');
};

interface MaterialOverlaySimpleProps {
    material: any; // Il materiale BABYLON selezionato
    onMaterialUpdated?: (material: any) => void; // Callback per notificare aggiornamenti del materiale
    onNameChanged?: (newName: string) => void; // Callback per notificare cambio nome specificatamente
    additionalFeatures?: any;
    onClose?: () => void; // Callback per chiudere l'overlay
    /** L'anteprima è appena stata riscattata: la lista che ci ha aperti può mostrarla subito. */
    onPreviewUpdated?: (materialID: string, dataUrl: string) => void;
    /** Porta la colonna di sinistra sugli oggetti e apre quello scelto: vedi la pastiglia. */
    onOpenObject?: (meshID: string) => void;
    /** «Dove sta?» — la freccetta della testata: accende la riga di questo materiale nella libreria. */
    onReveal?: () => void;
    /** La scena è cambiata con un gesto che non passa da un campo: chi ci sta sopra segni che c'è da salvare. */
    onSceneChanged?: () => void;
}

const UnsupportedNote = styled.p`
    padding: 20px;
    color: ${(props) => props.theme.app.primary};
    font-size: 12px;
    font-style: italic;
    text-align: center;
`;

// IL VELO SUL RITRATTO. Nero e glifo bianco in tutti e due i temi, e non è una dimenticanza:
// sotto c'è una fotografia, che chiara o scura resta quella — un velo che seguisse il tema
// sparirebbe sulla metà delle sfere.
const PreviewShot = styled.span`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    opacity: 0;
    transition: opacity 120ms ease;

    svg {
        width: 16px;
        height: 16px;
    }
`;

// IL RITRATTO IN TESTATA È ANCHE IL COMANDO, e sono due cose che si sono unite. La faccia del
// materiale stava due volte nello stesso pannello — una pallina tinta accanto al nome e la sfera
// vera nella riga sotto — e quella accanto al nome, cioè il posto dove si guarda per capire che
// cosa si sta toccando, era un segnaposto.
//
// Rifare la fotografia è un gesto raro — lo si fa dopo aver cambiato il materiale — e una riga
// con etichetta lo annunciava a ogni apertura, prima ancora del colore di base: il posto più in
// vista per la cosa che si fa meno. Adesso il gesto sta sulla cosa: si preme la sfera. Che si
// possa premere lo dice il velo, che compare passandoci sopra e resta acceso mentre scatta — è
// anche l'unico segno che sta succedendo.
const PreviewThumb = styled.button<{ $image: string | null; $color?: string | null }>`
    position: relative;
    flex: none;
    width: 34px;
    height: 34px;
    padding: 0;
    cursor: pointer;
    border-radius: 50%;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    background-image: ${(props) => (props.$image ? `url(${props.$image})` : materialSwatchFallback(props.$color))};
    background-position: center;
    background-size: cover;

    &:not(:disabled):hover ${PreviewShot}, &:focus-visible ${PreviewShot}, &[data-busy='true'] ${PreviewShot} {
        opacity: 1;
    }

    &:disabled {
        cursor: default;
    }
`;

const UpgradeBanner = styled(UpgradeDialog)`
    margin-bottom: 20px;
    p {
        margin: 0;
        padding: 0;
        line-height: 18px;
    }
    z-index: 10000 !important;
`;

// Il titolo di una SEZIONE non si apre e non si chiude (è la regola del prototipo): una sezione
// che si chiude nasconderebbe metà materiale dietro una maniglia che nessuno ha ragione di
// premere. A chiudersi sono i gruppi dentro di lei, che è il livello a cui la scelta serve.
// IL CAPITOLO NON SI DISEGNA PIÙ QUI. Era una `h4` scritta a mano, uguale a quella che World
// ed Effects si scrivevano per conto loro: tre copie della stessa parola in alto, con tre
// margini che avevano già cominciato a divergere. Ora è `PropertiesPanelSection`.

// LA BARRA STA IN CIMA AL PANNELLO, non dentro un capitolo: cerca fra TUTTI i parametri del
// materiale — anche quelli che stanno sotto la spina «Advanced» — e un campo messo dentro una
// card prometterebbe di cercare solo lì dentro.
const ParameterSearch = styled.div`
    margin: 0 12px 12px 0;
`;

// IL VUOTO RISPONDE. Un pannello che si svuota senza dire niente si legge come un pannello
// rotto, e chi cerca non sa se ha sbagliato parola o se quel parametro qui non esiste.
const NoMatch = styled.p`
    margin: 4px 12px 0 2px;
    color: ${panel.inkMuted};
    font-size: 12px;
`;

// IL FLUSSO SCELTO A MANO SI RICORDA, per la stessa ragione. La modalità si DEDUCE dal
// materiale (vedi `detectFinishMode`), ma passare a Separate su un materiale che non ha ancora
// caricato niente non lascia traccia su di lui: senza questa memoria la card tornerebbe a ORM
// al primo ridisegno, sotto le dita di chi sta per caricare la prima mappa.
const finishModeByMaterial = new Map<string, FinishMode>();

// E SI RICORDA ANCHE CIÒ CHE SI STACCA cambiando sponda, per rimetterlo se si torna indietro.
//
// CAMBIARE VISTA NON TOCCA IL MATERIALE quando di là non c'è ancora niente da rimettere
// (utente, 2026-09-14). Prima si staccava sempre: dopo un ricaricamento della pagina la
// memoria riparte vuota, e un clic di curiosità sull'altra sponda svuotava gli slot e
// anneriva il modello — le mappe erano recuperabili solo tornando indietro, e un salvataggio
// fatto lì in mezzo se le portava via davvero.
//
// Le due sponde però non possono essere accese INSIEME: lo shader moltiplica in fila — la
// ruvidità presa dal verde del pacchetto e poi di nuovo dalla mappa separata — e il materiale
// diventerebbe lucido senza che si capisca perché. Per questo il flusso si sceglie davvero
// nei due momenti in cui si tocca il materiale sul serio: quando si carica una mappa, e al
// SALVATAGGIO — dove resta solo l'ultima scelta e il resto viene via (vedi
// `commitFinishChoices`, chiamata da `SceneEditor`).
//
// E IL CAMBIO NON CHIEDE CONFERMA (utente, 2026-09-09). Una c’era, e contava le mappe che si
// staccavano: ma nella sponda dove si arriva quelle mappe non ci sono mai state — la loro
// casella è vuota — e tornando indietro ricompaiono. Una domanda che nel caso normale non
// protegge da niente insegna solo a rispondere «sì» senza leggere.

type FinishSide = Record<string, unknown>;
const finishMemoryByMaterial = new Map<string, Partial<Record<FinishMode, FinishSide>>>();

// LA ORM GUARDATA IN SEPARATE (QA, 2026-10-14). Entrando in Separate dalla ORM, senza separate da
// rimettere, si vede la stessa mappa canale per canale: non è un flusso, è uno sguardo. Uscendone così
// non si ricorda niente — prima si ricordava come «le separate», e al giro dopo tornava la ORM di
// prima anche dopo averla sostituita. Si ricorda solo ciò che lì si carica o si toglie: vedi sotto.
const ormViewInSeparate = new Set<string>();

// E DENTRO QUELLO SGUARDO SI POSSONO CARICARE DELLE MAPPE: una ruvidità sua, un metallo suo. Sono quelle,
// e solo quelle, le separate da ricordare; i canali che si è soltanto guardati restano della ORM, e al
// giro dopo mostrano quella di adesso — anche se nel frattempo è stata sostituita (QA, 2026-10-14).
const SEPARATE_SLOT_FIELDS: Record<string, string[]> = {
    ambient: ['ambientTexture', 'useAmbientInGrayScale'],
    microSurface: ['microSurfaceTexture'],
    metallic: ['metallicTexture', 'useMetallnessFromMetallicTextureBlue']
};
const separateSlotsByMaterial = new Map<string, Set<string>>();
const markSeparateSlot = (material: any, type: string) => {
    if (!SEPARATE_SLOT_FIELDS[type]) return;
    const key = material?.id ?? material?.name ?? '';
    separateSlotsByMaterial.set(key, (separateSlotsByMaterial.get(key) ?? new Set()).add(type));
};
/**
 * Le separate caricate sopra la ORM: tornando si posano su quella di adesso invece di sostituirla.
 * Svuotare tutto non è un flusso: senza nemmeno una mappa non c'è niente da ricordare, e si torna a
 * vedere la ORM.
 */
const readOwnSeparate = (material: any, key: string): FinishSide | undefined => {
    const slots = separateSlotsByMaterial.get(key);
    if (!slots?.size) return undefined;
    const side: FinishSide = {};
    slots.forEach((slot) => SEPARATE_SLOT_FIELDS[slot].forEach((field) => (side[field] = material[field])));
    return Object.keys(side).some((field) => field.endsWith('Texture') && side[field]) ? side : undefined;
};

// LA STESSA IMMAGINE, anche dopo un ricaricamento: in sessione i due slot tengono lo stesso oggetto,
// riletti dal salvataggio sono due texture con lo stesso indirizzo.
const sameImage = (a: any, b: any) => Boolean(a && b && (a === b || (a.url && a.url === b.url)));

// IL FLUSSO SI LEGGE DAI FLAG, PRIMA CHE DALLE MAPPE. Il verde, l'alfa o il rosso accesi sulla mappa
// del metallo dicono che è un pacchetto, e così un'occlusione presa dalla stessa immagine. Guardare
// solo se c'era un'ambient sbagliava proprio sul caso più comune — un glTF importato porta la stessa
// ORM anche come ambient, per l'occlusione — e il materiale si apriva in Separate, con la ORM nello
// slot «Metallic».
//
// IL BLU DA SOLO NON FA UN PACCHETTO (utente, 2026-09-15). Nelle separate è il canale da cui si legge
// la mappa del metallo — «Texture channel · Gray | B» — e un pacchetto senza la ruvidità nel verde
// non è la forma in cui una ORM arriva.
//
// UN'OCCLUSIONE PRESA DA UN'ALTRA IMMAGINE È DELLE SEPARATE (QA, 2026-10-12): un glTF con l'AO a parte si
// apriva in ORM con la R spenta sopra un'occlusione attiva, e accendere la R la sostituiva col rosso del
// pacchetto. Lo stesso per una mappa della ruvidità: in ORM non ha un posto.
const detectFinishMode = (material: any): FinishMode => {
    const metal = material?.metallicTexture;
    const ambient = material?.ambientTexture;
    if (material?.microSurfaceTexture || (ambient && metal && !sameImage(ambient, metal))) return 'separate';

    const packed =
        metal &&
        (material.useRoughnessFromMetallicTextureGreen ||
            material.useRoughnessFromMetallicTextureAlpha ||
            material.useAmbientOcclusionFromMetallicTextureRed ||
            sameImage(ambient, metal));

    if (packed) return 'orm';
    return ambient || (metal && material.useMetallnessFromMetallicTextureBlue) ? 'separate' : 'orm';
};

/** Il flusso di un materiale: quello scelto a mano, se c'è, altrimenti quello che si legge. */
const modeOf = (material: any): FinishMode => finishModeByMaterial.get(material?.id ?? material?.name ?? '') ?? detectFinishMode(material);

// UN PACCHETTO SI RICONOSCE GUARDANDOLO (utente, 2026-09-14). Lo slot del pacchetto e quello
// della mappa metallica sono lo stesso campo del materiale: passando alla vista ORM, la mappa
// del metallo caricata in Separate si vedeva lì dentro come se fosse una ORM, e non lo è.
//
// La differenza è nei pixel, non nel nome: una mappa del solo metallo è in scala di grigi — i tre
// canali portano lo stesso numero — mentre in un pacchetto occlusione, ruvidità e metallo sono
// tre immagini diverse. Quindi si leggono i pixel e si conta quanti hanno i canali davvero
// diversi; una manciata sparsa è rumore di compressione, non un pacchetto.
//
// La lettura passa dalla GPU, costa e si fa una volta sola per texture: l'esito resta qui, legato
// alla texture stessa, e se ne va con lei.
const CHANNEL_NOISE = 4;
const PACKED_MIN_SHARE = 0.01;
const PACKED_SAMPLES = 20000;

const packedByTexture = new WeakMap<object, boolean>();
const packedPending = new WeakSet<object>();

/** true se la mappa ha più canali, false se è in scala di grigi, undefined se non si sa ancora. */
const isPackedTexture = (texture: any): boolean | undefined => (texture ? packedByTexture.get(texture) : undefined);

const readPackedTexture = (texture: any, whenKnown: () => void) => {
    if (!texture || packedByTexture.has(texture) || packedPending.has(texture)) return;
    if (typeof texture.readPixels !== 'function') return;
    if (typeof texture.isReady === 'function' && !texture.isReady()) return;

    let reading: unknown;
    try {
        reading = texture.readPixels();
    } catch {
        return;
    }
    if (!reading) return;

    packedPending.add(texture);
    Promise.resolve(reading)
        .then((data: any) => {
            if (!data || !data.length) return;
            const pixels = Math.floor(data.length / 4);
            const step = Math.max(1, Math.floor(pixels / PACKED_SAMPLES)) * 4;
            let different = 0;
            let sampled = 0;
            for (let i = 0; i + 2 < data.length; i += step) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                if (Math.abs(r - g) > CHANNEL_NOISE || Math.abs(g - b) > CHANNEL_NOISE || Math.abs(r - b) > CHANNEL_NOISE) different++;
                sampled++;
            }
            if (sampled > 0) packedByTexture.set(texture, different / sampled > PACKED_MIN_SHARE);
        })
        .catch(() => undefined)
        .finally(() => {
            packedPending.delete(texture);
            whenKnown();
        });
};

/**
 * LO SLOT DEL PACCHETTO È VUOTO anche quando una mappa c'è, se quella mappa non è un pacchetto:
 * è la mappa del metallo delle Separate, che in questa vista non vuol dire niente. Finché la
 * lettura non ha risposto si mostra ciò che c'è, per non far lampeggiare la riga.
 */
const packSlotIsEmpty = (material: any) => !material?.metallicTexture || isPackedTexture(material.metallicTexture) === false;

// LE RIGHE DELLO SLOT, SVUOTATE. La prima `metallic-image` è la mappa e resta, senza miniatura né
// cestino, perché è da lì che si carica un pacchetto; la seconda è la versione mobile e non si
// disegna, come quando lo slot è vuoto davvero. Con loro se ne vanno scalatura e canale UV, che
// senza mappa non governano niente. L'ordine — prima la mappa, poi la mobile — è quello in cui il
// pannello le costruisce.
const hiddenPackRows = () => {
    let seen = 0;
    return {
        'metallic-image': (property: any) => (seen++ === 0 ? { ...property, images: null } : null),
        'metallic-texture-scaling': () => null,
        'metallic-uv-channel': () => null
    };
};

// L'OCCLUSIONE DEL PACCHETTO SI DICE COME LA DICE IL glTF: la stessa immagine anche nello slot
// ambient, letta in grigio — cioè dal rosso. Si faceva col flag del rosso, che a vedersi è
// identico, ma quel flag lo legge solo lo shader: l'export lo ignora, e l'AR partiva senza
// occlusione. Lo slot ambient invece il glTF lo conosce, e con la stessa immagine ne esce la sua
// forma canonica — una texture sola, per la ruvidità, il metallo e l'occlusione.
//
// Il flag del rosso resta letto, per i materiali salvati prima: la lettera li mostra accesi, e
// toccarla li porta nella forma nuova.
//
// L'OCCLUSIONE È DEL PACCHETTO SOLO SE È LA SUA IMMAGINE. Cambiare vista non stacca niente, e nella
// vista ORM la mappa AO delle separate accendeva la R di un pacchetto che non c'era.
const ormOcclusionOn = (material: any) =>
    Boolean(material?.useAmbientOcclusionFromMetallicTextureRed || sameImage(material?.ambientTexture, material?.metallicTexture));

const setOrmOcclusion = (material: any, on: boolean) => {
    material.useAmbientOcclusionFromMetallicTextureRed = false;
    material.ambientTexture = on ? (material.metallicTexture ?? null) : null;
    if (on) material.useAmbientInGrayScale = true;
};

// LE DUE SPONDE, DICHIARATE. `metallicTexture` sta in tutte e due e non è una svista: nel
// pacchetto è la mappa con i tre canali, in Separate è la mappa del solo metallo. Stesso campo, due
// significati — e proprio per questo va staccato passando di là. Lo stesso vale per `ambientTexture`:
// nel pacchetto è la ORM stessa, letta per l'occlusione (vedi `setOrmOcclusion`); in Separate è la
// mappa dell'occlusione. E per i flag che dicono da quale canale leggerle: il blu è la B del pacchetto
// o il canale della mappa del metallo, il grigio dell'ambient è la R dell'una o dell'altra, il verde è
// la G della ruvidità — nel pacchetto, o nelle separate quando la ruvidità si legge dalla mappa del
// metallo (QA, 2026-10-12: la stessa ORM vista in Separate, un canale per riga).
const FINISH_SIDES: Record<FinishMode, string[]> = {
    orm: [
        'metallicTexture',
        'useRoughnessFromMetallicTextureGreen',
        'useRoughnessFromMetallicTextureAlpha',
        'useMetallnessFromMetallicTextureBlue',
        'useAmbientOcclusionFromMetallicTextureRed',
        'ambientTexture',
        'useAmbientInGrayScale'
    ],
    separate: [
        'metallicTexture',
        'microSurfaceTexture',
        'ambientTexture',
        'useAmbientInGrayScale',
        'useMetallnessFromMetallicTextureBlue',
        'useRoughnessFromMetallicTextureGreen'
    ]
};

// I CAMPI DI UNA SPONDA SOLA: l'alfa e il rosso sono del pacchetto, la mappa della ruvidità è delle
// separate. Gli altri stanno in tutte e due — uno slot, una sola immagine, un canale da cui leggerla —
// e sono quelli che non si possono tenere doppi.
const other = (mode: FinishMode): FinishMode => (mode === 'orm' ? 'separate' : 'orm');
const FINISH_ONLY: Record<FinishMode, string[]> = {
    orm: FINISH_SIDES.orm.filter((field) => !FINISH_SIDES.separate.includes(field)),
    separate: FINISH_SIDES.separate.filter((field) => !FINISH_SIDES.orm.includes(field))
};

const readFinishSide = (material: any, mode: FinishMode): FinishSide => Object.fromEntries(FINISH_SIDES[mode].map((field) => [field, material?.[field]]));

const writeFinishSide = (material: any, mode: FinishMode, side?: FinishSide) => {
    FINISH_SIDES[mode].forEach((field) => {
        const value = side ? side[field] : undefined;
        material[field] = value ?? (field.endsWith('Texture') ? null : false);
    });
};

// LE TEXTURE CHE L'ALTRA SPONDA RICORDA. Sostituendo una mappa il motore libera la vecchia se nessun
// materiale la usa più, e una sponda in memoria non è un materiale: tornando di là si rimetteva una
// texture morta. Quella che sta in memoria non si butta — nello slot va una sua copia, che il motore può
// buttare, e l'originale resta a chi lo ricorda.
const rememberedTextures = (material: any): unknown[] =>
    Object.values(finishMemoryByMaterial.get(material?.id ?? material?.name ?? '') ?? {}).flatMap((side) => Object.values(side ?? {}));

const spareRemembered = (material: any, field: string) => {
    const texture = material?.[field];
    if (texture && typeof texture.clone === 'function' && rememberedTextures(material).includes(texture)) material[field] = texture.clone();
};

// UNA SPONDA DA RICORDARE è una che porta delle mappe: il pacchetto per la ORM, una qualunque delle tre
// per le separate. È più largo di `finishSideHasContent`, che decide invece quando un flusso è SCELTO.
const finishSideHasMaps = (material: any, mode: FinishMode) =>
    mode === 'orm' ? !packSlotIsEmpty(material) : Boolean(material?.microSurfaceTexture || material?.ambientTexture || material?.metallicTexture);

// SCEGLIERE UN FLUSSO VUOL DIRE TOGLIERE L'ALTRO — solo ciò che è suo soltanto, perché gli slot
// condivisi sono già occupati da quello scelto. Quel che si toglie resta in memoria, così finché
// la pagina è aperta tornare indietro rimette tutto.
//
// MA UN FLUSSO VUOTO NON SCEGLIE NIENTE (utente, 2026-09-14). Guardare come sarebbe di là è un
// gesto gratis, e deve restarlo anche se poi si salva: se la sponda scelta non porta una mappa
// sua, non c'è nessuna scelta da rispettare e quella di prima resta dov'è. Senza questa riga un
// clic di curiosità seguito da un salvataggio cancellava la mappa della ruvidità.
const finishSideHasContent = (material: any, mode: FinishMode) => (mode === 'orm' ? !packSlotIsEmpty(material) : Boolean(material?.microSurfaceTexture));

const commitFinishChoice = (material: any, mode: FinishMode) => {
    const fields = FINISH_ONLY[other(mode)];
    if (!material || !fields.some((field) => material[field])) return false;
    if (!finishSideHasContent(material, mode)) return false;

    const key = material.id ?? material.name ?? '';
    const remembered = finishMemoryByMaterial.get(key) ?? {};
    // Delle separate si ricorda solo una ruvidità caricata lì: quella che si guardava dalla ORM non è loro.
    if (mode !== 'orm' || separateSlotsByMaterial.get(key)?.has('microSurface'))
        remembered[other(mode)] = { ...(remembered[other(mode)] ?? {}), ...Object.fromEntries(fields.map((field) => [field, material[field]])) };
    finishMemoryByMaterial.set(key, remembered);

    fields.forEach((field) => (material[field] = field.endsWith('Texture') ? null : false));
    return true;
};

/**
 * AL SALVATAGGIO UN MATERIALE HA UN FLUSSO SOLO (utente, 2026-09-14: «ripulire il superfluo e
 * lasciare solo l'ultima scelta»). Durante il lavoro le due sponde possono convivere — una
 * attaccata e una in memoria — perché cambiare vista non deve distruggere niente; ma ciò che
 * finisce sul server è la scelta fatta per ultima, e le mappe rimaste fuori dagli slot il core
 * le pota da sé.
 *
 * Tocca solo i materiali su cui il flusso è stato scelto a mano: gli altri sono come sono stati
 * caricati, e non spetta a noi riordinarli.
 */
export const commitFinishChoices = (materials: any[]) => {
    materials?.forEach((material) => {
        const mode = finishModeByMaterial.get(material?.id ?? material?.name ?? '');
        if (mode) commitFinishChoice(material, mode);
    });

    // LE MAPPE CHE RESTAVANO SOLO IN MEMORIA SE NE VANNO col salvataggio: il salvataggio del motore scorre
    // le texture della scena, e una tenuta da parte per tornare indietro sarebbe salvata come se servisse.
    // Si guardano tutti i materiali della scena, non soltanto quelli dell'elenco: una mappa condivisa con
    // un materiale di design è ancora usata.
    const scene = materials?.[0]?.getScene?.();
    const inUse = new Set((scene?.materials ?? materials ?? []).flatMap((material: any) => material?.getActiveTextures?.() ?? []));
    finishMemoryByMaterial.forEach((sides) =>
        Object.values(sides).forEach((side) =>
            Object.values(side ?? {}).forEach((value: any) => {
                if (value && typeof value.dispose === 'function' && !inUse.has(value)) value.dispose();
            })
        )
    );

    finishMemoryByMaterial.clear();
    ormViewInSeparate.clear();
    separateSlotsByMaterial.clear();
};

type FinishOverrides = Record<string, (property: PropertiesPanelLeafConfig) => PropertiesPanelLeafConfig | null>;

// LE LETTERE DEI CANALI, costruite qui e non nel generatore. Il generatore ha tre spunte con la frase
// intera — «Use roughness from green» e compagne —: nel mockup sono le R G B sulle righe dei tre valori
// del pacchetto. E le mappe separate hanno un «Texture channel» che il generatore non ha affatto.
// Toccarle vuol dire toccare il flusso, e il flusso lo conosce questo file.
//
// SENZA LA MAPPA CHE LE ALIMENTA LE LETTERE SI SPENGONO, e con loro il valore dell'occlusione (mockup:
// `gate` e `dim`). Ruvidità e metallo restano vivi: una superficie è ruvida o metallica anche senza una
// mappa che lo moduli, mentre l'occlusione è l'ombra che la geometria si fa addosso — senza una mappa
// che la descriva non c'è niente da dosare.
//
// SONO PULSANTI, e un pulsante non emette `change`: il «da salvare» lo segna `changed`.
const finishRows = (
    material: any,
    mode: FinishMode,
    changed: () => void,
    /** L'anteprima della mappa del metallo: nelle separate è anche quella da cui la ruvidità può leggere. */
    metalPreview: string | null
): { overrides: FinishOverrides; extra: PropertiesPanelLeafConfig[] } => {
    const setFlag = (field: string, on: boolean) => {
        material[field] = on;
        changed();
    };

    if (mode === 'orm') {
        const noPack = packSlotIsEmpty(material);
        const lettered =
            (tag: PropertiesPanelAxisToggle, dim = false) =>
            (property: PropertiesPanelLeafConfig): PropertiesPanelLeafConfig =>
                property.type === 'range' ? { ...property, tag: { ...tag, disabled: noPack }, disabled: property.disabled || (dim && noPack) } : property;

        return {
            extra: [],
            overrides: {
                'ambient-strength': lettered(
                    {
                        id: 'orm-occlusion-red',
                        axis: 'x',
                        letter: 'R',
                        ariaLabel: T._('Occlusion from the red channel', 'Admin'),
                        pressed: ormOcclusionOn(material),
                        onChange: (on) => {
                            setOrmOcclusion(material, on);
                            changed();
                        }
                    },
                    true
                ),
                roughness: lettered({
                    id: 'orm-roughness-green',
                    axis: 'y',
                    letter: 'G',
                    ariaLabel: T._('Roughness from the green channel', 'Admin'),
                    pressed: Boolean(material.useRoughnessFromMetallicTextureGreen),
                    onChange: (on) => setFlag('useRoughnessFromMetallicTextureGreen', on)
                }),
                metallic: lettered({
                    id: 'orm-metallic-blue',
                    axis: 'z',
                    letter: 'B',
                    ariaLabel: T._('Metallic from the blue channel', 'Admin'),
                    pressed: Boolean(material.useMetallnessFromMetallicTextureBlue),
                    onChange: (on) => setFlag('useMetallnessFromMetallicTextureBlue', on)
                })
            }
        };
    }

    // «GRAY | R» E «GRAY | B» SONO DUE FLAG DI BABYLON, LETTI PER QUELLO CHE FANNO (verificato nello
    // shader, 5.50.1). `useAmbientInGrayScale`, nonostante il nome, prende l'occlusione dal solo rosso;
    // spento moltiplica l'immagine intera, che su una mappa in grigio è il grigio. Il blu legge il metallo
    // dal blu, spento dal rosso — di nuovo il grigio, su una mappa in grigio.
    //
    // LA RUVIDITÀ SI LEGGE DA DUE POSTI, e lo shader non ne conosce un terzo: dalla sua mappa, soltanto dal
    // rosso (`microSurfaceTexel.r`), oppure dal VERDE DELLA MAPPA DEL METALLO
    // (`useRoughnessFromMetallicTextureGreen`). La G quindi si sceglie quando le due mappe sono la stessa
    // immagine — chi arriva dalla ORM, e vuole vederla su tre righe (QA, 2026-10-12, supera la scelta del
    // 2026-09-15). Con una mappa della ruvidità sua la G resta in vista spenta, e dice perché.
    const roughFromMetal = Boolean(material.useRoughnessFromMetallicTextureGreen && material.metallicTexture && !material.microSurfaceTexture);
    const roughIsMetal = roughFromMetal || sameImage(material.microSurfaceTexture, material.metallicTexture);

    const textureChannel = (row: {
        id: string;
        /** Il flag che legge dal canale. Senza, la lettera la governano `reads`, `onGray` e `onLetter`. */
        field?: string;
        reads?: boolean;
        onGray?: () => void;
        onLetter?: () => void;
        hasMap: boolean;
        axis: 'x' | 'y' | 'z';
        letter: string;
        channel: string;
        reason?: string;
    }): PropertiesPanelLeafConfig => {
        const field = row.field;
        const reads = row.reads ?? (field ? Boolean(material[field]) : false);
        const onGray = row.onGray ?? (field ? () => setFlag(field, false) : undefined);
        const onLetter = row.onLetter ?? (field ? () => setFlag(field, true) : undefined);
        return {
            type: 'axis-toggles',
            id: row.id,
            label: T._('Texture channel', 'Admin'),
            exclusive: true,
            disabled: !row.hasMap,
            toggles: [
                {
                    id: `${row.id}-gray`,
                    letter: T._('Gray', 'Admin'),
                    ariaLabel: T._('Grayscale', 'Admin'),
                    pressed: !reads,
                    onChange: onGray
                },
                {
                    id: `${row.id}-${row.axis}`,
                    axis: row.axis,
                    letter: row.letter,
                    ariaLabel: row.channel,
                    pressed: reads,
                    reason: onLetter ? undefined : row.reason,
                    onChange: onLetter
                }
            ]
        };
    };

    // LO SLOT DELLA RUVIDITÀ MOSTRA LA MAPPA DA CUI LA RUVIDITÀ SI LEGGE: quando è il verde del metallo, è
    // quella. La prima riga è la mappa, la seconda la sua versione mobile, che qui non c'entra.
    let roughnessImageRows = 0;

    return {
        overrides: {
            'ambient-strength': (property) => (property.type === 'range' ? { ...property, disabled: property.disabled || !material.ambientTexture } : property),
            ...(roughFromMetal
                ? {
                      'microSurface-image': (property: PropertiesPanelLeafConfig) =>
                          roughnessImageRows++ === 0 && property.type === 'image'
                              ? {
                                    ...property,
                                    images: metalPreview ? [{ fileName: '', src: metalPreview }] : null,
                                    onDelete: () => setFlag('useRoughnessFromMetallicTextureGreen', false)
                                }
                              : property
                  }
                : {})
        },
        extra: [
            textureChannel({
                id: 'ambient-texture-channel',
                field: 'useAmbientInGrayScale',
                hasMap: Boolean(material.ambientTexture),
                axis: 'x',
                letter: 'R',
                channel: T._('Red channel', 'Admin')
            }),
            textureChannel({
                id: 'roughness-texture-channel',
                hasMap: Boolean(material.microSurfaceTexture) || roughFromMetal,
                axis: 'y',
                letter: 'G',
                channel: T._('Green channel', 'Admin'),
                reads: roughFromMetal,
                // Gray legge la stessa immagine come mappa della ruvidità, cioè dal rosso.
                onGray: roughFromMetal
                    ? () => {
                          material.microSurfaceTexture = material.metallicTexture;
                          setFlag('useRoughnessFromMetallicTextureGreen', false);
                      }
                    : undefined,
                // G legge il verde della mappa del metallo: possibile solo se è la stessa immagine.
                onLetter: roughIsMetal
                    ? () => {
                          material.microSurfaceTexture = null;
                          setFlag('useRoughnessFromMetallicTextureGreen', true);
                      }
                    : undefined,
                reason: T._('Only from the metallic map: a roughness map of its own is read from its red channel', 'Admin')
            }),
            textureChannel({
                id: 'metallic-texture-channel',
                field: 'useMetallnessFromMetallicTextureBlue',
                hasMap: Boolean(material.metallicTexture),
                axis: 'z',
                letter: 'B',
                channel: T._('Blue channel', 'Admin')
            })
        ]
    };
};

const titledChapters = (sections: PbrPanelSection[], remountKey?: string) =>
    sections.map((section) => (
        <PropertiesPanelSection key={section.id} title={section.label}>
            <PropertiesPanel key={remountKey} properties={section.groups} />
        </PropertiesPanelSection>
    ));

// I capitoli di una tab: con uno solo il nome lo dice già la tab, e ripeterlo sopra le card sarebbe la
// stessa parola due volte.
const chapters = (sections: PbrPanelSection[]) => (sections.length === 1 ? <PropertiesPanel properties={sections[0].groups} /> : titledChapters(sections));

const MaterialOverlaySimple: React.FC<MaterialOverlaySimpleProps> = ({
    material,
    onMaterialUpdated,
    onNameChanged,
    additionalFeatures,
    onClose,
    onPreviewUpdated,
    onOpenObject,
    onReveal,
    onSceneChanged
}) => {
    const [zkProduct, zkScene, sceneManager, sceneEditor, designManager] = usePreview3DStore((x) => [
        x.zkProduct,
        x.zkScene,
        x.sceneManager,
        x.sceneEditor,
        x.designManager
    ]);

    // Spostato getTextureUrls dentro il componente per accedere a zkScene
    // OGNI STRADA PORTA A UN INDIRIZZO RELATIVO ALLA SCENA, non solo l’ultima. Le prime
    // quattro tornavano la stringa così com’era — il percorso dentro la cartella della scena —
    // e il browser la risolveva contro il backoffice: da lì le miniature rotte accanto a
    // texture che in 3D si vedono benissimo. La risoluzione è una sola e vale per tutte.
    const getTextureUrls = useCallback(
        (texture: BABYLON.BaseTexture): string[] => {
            const rootUrl = zkScene?.get('rootUrl') || '';
            const resolve = (url: string) => sceneAssetUrl(rootUrl, url);
            const isTextureWithFiles = (texture: BABYLON.BaseTexture): texture is BABYLON.BaseTexture & { _files: string[] } =>
                '_files' in texture && !!texture._files && Array.isArray(texture._files);

            const isTextureWithUrl = (texture: BABYLON.BaseTexture): texture is BABYLON.BaseTexture & { url: string } => 'url' in texture && !!texture.url;

            if (isTextureWithFiles(texture) && texture._files.length > 1) {
                return texture._files.map(resolve);
            }
            if (texture.metadata && texture.metadata.fileUrl) {
                return [resolve(texture.metadata.fileUrl)];
            }
            if (isTextureWithUrl(texture) && texture.url.startsWith('data:')) {
                return [texture.url];
            }
            if (texture.getInternalTexture && texture.getInternalTexture()?.url) {
                const textureUrl = texture.getInternalTexture()?.url;
                return textureUrl ? [resolve(textureUrl)] : [];
            }
            if (isTextureWithUrl(texture) && texture.url) {
                // Check if materialMapping exists before accessing its properties
                if (
                    (texture as any).materialMapping &&
                    (texture as any).materialMapping.libraryMaterialID &&
                    (texture as any).materialMapping.libraryMaterialRootUrl
                ) {
                    const materialMapping = (texture as any).materialMapping;
                    const idx = texture.url.indexOf(materialMapping.libraryMaterialID.replace(/-/g, ''));
                    if (idx !== -1) return [materialMapping.libraryMaterialRootUrl + texture.url];
                }
                return [resolve(texture.url)];
            }
            return [];
        },
        [zkScene]
    );

    const [loadingTextures, setLoadingTextures] = useState<Record<string, boolean>>({});
    const [, setUpdate] = useState(false);
    const materialsPropertiesCreator = useMaterialsPropertiesCreator();
    const { showDialog, closeDialog } = useDialogManager();
    const { ecommerceName, sellerMarketplace, updateSubscription } = useSubscriptionData();

    // L'anteprima del materiale aperto. Si riscatta solo quando qualcuno lo chiede:
    // rigenerarla a ogni modifica di proprietà era il disegno di prima, ed è il motivo per cui
    // l'avevano spenta — ogni scatto smonta e rimonta l'environment della scena, e farlo
    // mentre trascini uno slider è insostenibile.
    const [preview, setPreview] = useState<string | null>(null);
    const [isCapturingPreview, setIsCapturingPreview] = useState(false);

    useEffect(() => {
        setPreview(material?.id ? readMaterialPreview(sceneEditor, material.id) : null);
    }, [material?.id, sceneEditor]);

    // Due passi e non uno: lo scatto si mostra appena c'è, e se il caricamento non riesce lo
    // si dice. L'hai chiesto tu, quindi hai diritto sia di vederlo sia di sapere che non
    // sopravviverà al salvataggio.
    const capturePreview = useCallback(async () => {
        if (!sceneEditor || !material?.id || isCapturingPreview) return;

        setIsCapturingPreview(true);
        const dataUrl = await captureMaterialPreview(sceneEditor, material.id);

        if (!dataUrl) {
            setIsCapturingPreview(false);
            showErrorNotification({ message: T._('Could not generate the material preview.', 'Admin') });
            return;
        }

        setPreview(dataUrl);
        onPreviewUpdated?.(material.id, dataUrl);

        const url = await saveMaterialPreview(sceneEditor, material.id, dataUrl);
        setIsCapturingPreview(false);

        if (!url) showErrorNotification({ message: T._('The preview was generated but could not be saved.', 'Admin') });
    }, [sceneEditor, material, isCapturingPreview, onPreviewUpdated]);

    const renderDebounced = useCallback(
        debounce(() => designManager?.render(), 100),
        [designManager]
    );

    const updateState = useCallback(
        <T extends BABYLON.Material>(material: T, refreshMaterials?: boolean) => {
            const isDesignMaterial = zkProduct
                ?.get('scenes')
                .at(0)
                .get('meshSides')
                ?.toArray()
                .some((meshSide) => meshSide.get('materialID') === material.id);

            // Always re render because the base design material uses the same metallic/roughness of the base material
            if (isDesignMaterial) {
                renderDebounced();
            }

            setUpdate((prev) => !prev);
            sceneEditor?.render();
        },
        [sceneManager, zkProduct, renderDebounced, sceneEditor]
    );

    // IL FOTOGRAMMA GIUSTO ARRIVA QUANDO LO SHADER È PRONTO (QA, 2026-10-12). Un flag che cambia lo shader —
    // le lettere R G B della ORM, Refraction, Translucency — fa ricompilare l'effetto del materiale, e Babylon
    // intanto disegna con quello vecchio. Il palco disegna solo su richiesta, e l'unico fotogramma chiesto
    // cadeva prima che il nuovo fosse pronto: l'effetto non compariva, e un secondo clic sembrava servire —
    // trovava la compilazione già fatta. Si chiede un fotogramma in più, a compilazione finita.
    useEffect(() => {
        const created = (material as any)?.onEffectCreatedObservable;
        if (!created || !sceneEditor) return;

        const observer = created.add(({ effect }: { effect: BABYLON.Effect }) => effect.executeWhenCompiled(() => sceneEditor.setRenderingNeeded()));
        return () => created.remove(observer);
    }, [material, sceneEditor]);

    const isTextureLoading = useCallback(
        <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            return loadingTextures[generateTextureUniqueId(material, type, mobile)];
        },
        [loadingTextures]
    );

    const uploadTexture = useCallback(
        async <T extends BABYLON.Material>(material: T, type: string, mobile: boolean, files: FileList) => {
            if (!sceneEditor) {
                throw new Error('Scene editor is not available');
            }

            if (files.length === 0) {
                throw new Error('No files selected');
            }

            if (type === 'reflection' && !additionalFeatures?.customEnvironmentTexture) {
                // Chiudo l'overlay prima di mostrare la dialog
                if (onClose) {
                    onClose();
                }

                showDialog(
                    'Upgrade_Plan',
                    <UpgradeBanner
                        marketPlace={sellerMarketplace ?? undefined}
                        message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'Grow')}
                        onUpgradeCompleted={async () => {
                            updateSubscription();
                        }}
                    />
                );
                return;
            }

            const uniqueId = generateTextureUniqueId(material, type, mobile);
            setLoadingTextures((prev) => ({ ...prev, [uniqueId]: true }));

            // SE LA MAPPA VA NEL PACCHETTO si decide prima di caricarla: dopo c'è già, e non si
            // saprebbe più se è arrivata in uno slot vuoto o al posto di un'altra.
            const pbr = material as any;
            const intoPack = type === 'metallic' && !mobile && modeOf(pbr) === 'orm';
            const replaced: any[] = intoPack ? [pbr.metallicTexture, pbr.ambientTexture] : [];
            // UNA MAPPA DEL METALLO NUOVA STACCA LA RUVIDITÀ CHE NE LEGGEVA IL VERDE, nelle separate: quel
            // verde era dell'immagine di prima, e da una mappa in grigio si leggerebbe il metallo.
            const roughReadsMetal =
                !mobile && type === 'metallic' && modeOf(pbr) === 'separate' && Boolean(pbr.useRoughnessFromMetallicTextureGreen) && !pbr.microSurfaceTexture;
            // UNA MAPPA SEPARATA IN UNO SLOT VUOTO SI LEGGE IN GRIGIO, che è il default del foglio di
            // mappatura; al posto di un'altra mappa resta il canale che era stato scelto.
            const separateWasEmpty =
                !mobile && modeOf(pbr) === 'separate' && ((type === 'ambient' && !pbr.ambientTexture) || (type === 'metallic' && !pbr.metallicTexture));
            // LA LIGHTMAP NASCE SU UV0 (QA, 2026-10-12). Il motore la mette sempre sul secondo canale, anche
            // al posto di una che stava sul primo: il mapping risultava già scavalcato, e un modello senza un
            // secondo set di UV campiona la mappa in un punto solo. Al posto di un'altra resta il canale di
            // quella; il motore non cambia, perché serve anche le pagine di prima.
            if (!mobile && modeOf(pbr) === 'separate') markSeparateSlot(pbr, type);
            const lightmapUV = type === 'lightmap' && !mobile ? (pbr.lightmapTexture?.coordinatesIndex ?? 0) : undefined;

            try {
                // Una mappa che l'altro flusso ricorda non si fa buttare al motore: vedi `spareRemembered`.
                if (!mobile) spareRemembered(pbr, type + 'Texture');

                if (mobile) {
                    await sceneEditor.loadMobileTextureFilesAsync(Array.from(files), material, type, false);
                } else {
                    await sceneEditor.loadTextureFilesAsync(Array.from(files), material, type, false);
                }

                if (lightmapUV !== undefined && pbr.lightmapTexture) pbr.lightmapTexture.coordinatesIndex = lightmapUV;

                // CARICARE UNA MAPPA È SCEGLIERE IL FLUSSO: da qui in poi le due sponde non possono
                // più convivere sul materiale, o lo shader moltiplicherebbe due ruvidità.
                commitFinishChoice(pbr, modeOf(pbr));

                if (separateWasEmpty && type === 'ambient') pbr.useAmbientInGrayScale = false;
                if (separateWasEmpty && type === 'metallic') pbr.useMetallnessFromMetallicTextureBlue = false;

                // UNA MAPPA DELLA RUVIDITÀ SPEGNE LA LETTURA DAL VERDE DEL METALLO: lo shader le
                // moltiplicherebbe in fila. Vale anche per la mappa del metallo nuova (`roughReadsMetal`).
                if ((type === 'microSurface' && !mobile) || roughReadsMetal) pbr.useRoughnessFromMetallicTextureGreen = false;

                if (intoPack) {
                    // UNA ORM ARRIVA SEMPRE CON I TRE CANALI ACCESI (QA, 2026-10-12), perché è questo che vuol
                    // dire ORM: occlusione nel rosso, ruvidità nel verde, metallo nel blu. Con le spunte spente
                    // lo shader leggeva il metallo dal rosso e ignorava il verde — e l'export, che le spunte
                    // non le guarda, mostrava in AR un altro materiale. Prima succedeva solo in uno slot vuoto,
                    // e al posto di un'altra mappa si ereditavano le lettere di quella: anche della mappa del
                    // solo metallo, con la R spenta.
                    pbr.useRoughnessFromMetallicTextureGreen = true;
                    pbr.useMetallnessFromMetallicTextureBlue = true;
                    pbr.useRoughnessFromMetallicTextureAlpha = false;
                    setOrmOcclusion(pbr, true);

                    // La mappa di prima il motore la tiene in vita finché sta nello slot ambient: ora
                    // che non la usa più nessuno, si libera — se non è l'altro flusso a ricordarla.
                    replaced.forEach((texture) => {
                        if (
                            texture &&
                            !rememberedTextures(pbr).includes(texture) &&
                            sceneEditor.scene?.textures?.includes(texture) &&
                            !sceneEditor.isTextureUsed(texture)
                        )
                            texture.dispose();
                    });
                }
            } catch (error) {
                console.error(error);
                throw new Error('Failed to upload texture', {
                    cause: error
                });
            } finally {
                setLoadingTextures((prev) => ({ ...prev, [uniqueId]: false }));
                updateState(material);
                onSceneChanged?.();
            }
        },
        [sceneEditor, updateState, onSceneChanged]
    );

    const removeTexture = useCallback(
        async <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            if (!sceneEditor) {
                throw new Error('Scene editor is not available');
            }

            // TOGLIERE LA ORM TOGLIE ANCHE LA SUA OCCLUSIONE: nello slot ambient c'è la stessa
            // immagine, e da sola resterebbe un'AO senza più la mappa da cui viene. Il flusso si
            // legge prima di togliere: dopo, senza la mappa, un'ambient rimasta direbbe Separate.
            const fromPack = type === 'metallic' && !mobile && modeOf(material) === 'orm';
            if (!mobile && modeOf(material) === 'separate') markSeparateSlot(material, type);

            if (mobile) {
                sceneEditor.removeMaterialMobileTexture(material, type);
            } else {
                sceneEditor.removeMaterialTexture(material, type);
                if (fromPack && (material as any).ambientTexture) sceneEditor.removeMaterialTexture(material, 'ambient');
                // Senza la mappa del metallo il suo verde non c'è più: la ruvidità non lo legge.
                if (type === 'metallic') (material as any).useRoughnessFromMetallicTextureGreen = false;
            }

            updateState(material);

            // IL CESTINO È UN PULSANTE, e un pulsante non emette `change`: togliere una mappa
            // cambiava il materiale senza che nessuno lo segnasse da salvare.
            onSceneChanged?.();
        },
        [sceneEditor, updateState, onSceneChanged]
    );

    // DOVE STA LA VERSIONE MOBILE DI UNA TEXTURE, e sono due posti soli: `metadata.mobileUrl` per
    // un'immagine, `metadata.mobileFiles` per una cube map. Li riempiono `setMobileTextureImage` e
    // `setMobileCubeTextureImages` del motore, e un terzo posto non esiste — per questo la ricerca
    // finisce qui invece di continuare altrove.
    //
    // SI RISOLVE COME QUELLA DESKTOP, ed è il punto: la mappa desktop ha cinque modi di trovare la
    // propria immagine e la mobile ne aveva uno, con una regola tutta sua — «se non comincia per
    // http, incollaci davanti la radice». Un `blob:` o un `data:`, che è la forma di una texture
    // appena caricata e non ancora salvata, diventavano così un indirizzo che non esiste: nessuna
    // anteprima, e nessun modo di capire perché. La radice ora la mette `sceneAssetUrl`, che
    // riconosce lo schema — ed è la stessa che risolve la mappa desktop.
    //
    // (Resta aperto di là il caso dei materiali importati dalla libreria, la cui versione mobile
    //  vuole la radice del materiale e non quella della scena: https://www.wrike.com/open.htm?id=1712281262)
    const getMobileTextureUrls = useCallback(
        (texture: any): string[] => {
            const rootUrl = zkScene?.get('rootUrl') || '';
            const files: string[] = texture?.metadata?.mobileFiles ?? [];

            if (files.length > 0) {
                return files.map((file) => sceneAssetUrl(rootUrl, file));
            }

            const url: string | undefined = texture?.metadata?.mobileUrl;
            return url ? [sceneAssetUrl(rootUrl, url)] : [];
        },
        [zkScene]
    );

    const getTexturePreviewUrl = useCallback(
        <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            if (!sceneEditor || !zkScene) {
                return null;
            }

            const texture = sceneEditor.getMaterialTexture(material, type);
            if (!texture) {
                return null;
            }

            const urls = mobile ? getMobileTextureUrls(texture) : getTextureUrls(texture);

            return urls[0] ?? null;
        },
        [sceneEditor, zkScene, getTextureUrls, getMobileTextureUrls]
    );

    // Funzione stabile per l'onChange del nome materiale
    const handleMaterialNameChange = useCallback(
        (newValue: string) => {
            if (!material) {
                return;
            }

            // Aggiorna il nome direttamente sul materiale BABYLON
            material.name = newValue || 'Material';

            // Chiama updateState per aggiornare l'UI dell'overlay
            updateState(material);

            // Notifica immediatamente il cambiamento del nome al parent
            if (onNameChanged) {
                onNameChanged(material.name);
            }

            // Notifica il sistema che i materiali sono stati aggiornati per sincronizzare l'albero
            if (onMaterialUpdated) {
                setTimeout(() => {
                    onMaterialUpdated(material);
                }, 50); // Small delay to ensure material name is fully updated
            }
        },
        [material, updateState, onMaterialUpdated, onNameChanged]
    );

    // Helpers per il materialsPropertiesCreator
    const helpers = useMemo(() => {
        if (!sceneEditor || !sceneManager) {
            return null;
        }
        return {
            sceneEditor,
            sceneManager,
            updateState,
            isTextureLoading,
            uploadTexture,
            removeTexture,
            getTexturePreviewUrl,
            handleMaterialNameChange
        };
    }, [sceneEditor, sceneManager, updateState, isTextureLoading, uploadTexture, removeTexture, getTexturePreviewUrl, handleMaterialNameChange]);

    const materialKey = material?.id ?? material?.name ?? '';

    const [finishMode, setFinishMode] = useState<FinishMode>('orm');

    useEffect(() => setFinishMode(modeOf(material)), [materialKey]);

    // LA RICERCA È DI QUESTO MATERIALE, e con lui finisce: cambiando soggetto il campo si
    // svuota. Portarsi dietro la parola di prima vorrebbe dire aprire un materiale nuovo già
    // filtrato, e sembrerebbe un materiale con tre parametri.
    const [parameterFilter, setParameterFilter] = useState('');
    useEffect(() => setParameterFilter(''), [materialKey]);

    // La mappa nello slot del pacchetto si guarda solo quando la vista ORM la mostra: è una
    // lettura dalla GPU, e su una scena intera non vale la pena farla per materiali che nessuno
    // sta aprendo. Quando la risposta arriva, la card si ridisegna.
    const [, redrawAfterRead] = useReducer((count: number) => count + 1, 0);
    // Dopo «Apply to selection» la pastiglia degli oggetti si riconta: nessun campo è cambiato.
    const [, redraw] = useReducer((count: number) => count + 1, 0);
    useEffect(() => {
        if (finishMode === 'orm') readPackedTexture((material as any)?.metallicTexture, redrawAfterRead);
    }, [finishMode, materialKey, (material as any)?.metallicTexture]);

    const changeFinishMode = useCallback(
        (next: FinishMode) => {
            if (next === finishMode) return;

            const previous: FinishMode = finishMode;
            const remembered = finishMemoryByMaterial.get(materialKey) ?? {};
            const back = remembered[next];

            // LA SPONDA CHE SI LASCIA SI RICORDA, anche quando di là non c'è niente da rimettere (QA,
            // 2026-10-12): è quella a cui si torna. Prima si ricordava solo nello scambio, e la prima volta
            // lo scambio non c'è: ciò che si caricava di là riscriveva gli slot condivisi, e tornando la ORM
            // non c'era più. I due flussi sono indipendenti: si torna a quello che c'era, non a un misto.
            //
            // MA DELLE SEPARATE SI RICORDA SOLO CIÒ CHE LÌ SI È CARICATO O TOLTO (QA, 2026-10-14): vedi
            // `separateSlotsByMaterial`. Un materiale che si è aperto già in Separate, con le sue mappe, le
            // ha tutte sue; uno che in Separate ci è arrivato dalla ORM — anche prima di un ricaricamento
            // del codice, che svuota le memorie — si riconosce dai flag del pacchetto ancora accesi.
            if (previous === 'separate') {
                const tracked = ormViewInSeparate.has(materialKey) || separateSlotsByMaterial.has(materialKey);
                if (!tracked && detectFinishMode(material) === 'separate')
                    Object.keys(SEPARATE_SLOT_FIELDS).forEach((slot) => (material as any)[SEPARATE_SLOT_FIELDS[slot][0]] && markSeparateSlot(material, slot));
                const own = readOwnSeparate(material, materialKey);
                if (own) remembered.separate = own;
                else delete remembered.separate;
                ormViewInSeparate.delete(materialKey);
            } else if (finishSideHasMaps(material, previous)) remembered[previous] = readFinishSide(material, previous);
            finishMemoryByMaterial.set(materialKey, remembered);

            if (next === 'separate') {
                // ENTRANDO IN SEPARATE SI VEDE LA ORM DI ADESSO canale per canale, e sopra si posano gli slot
                // che lì si erano caricati. Una ruvidità o un metallo propri staccano la lettura dal verde, che
                // lo shader moltiplicherebbe con loro.
                ormViewInSeparate.add(materialKey);
                if (back) {
                    Object.keys(back).forEach((field) => ((material as any)[field] = back[field]));
                    if (back.microSurfaceTexture || 'metallicTexture' in back) material.useRoughnessFromMetallicTextureGreen = false;
                    if ('metallicTexture' in back) material.useRoughnessFromMetallicTextureAlpha = false;
                    delete remembered[next];
                } else separateSlotsByMaterial.delete(materialKey);
            } else if (back && finishSideHasMaps(back, next)) {
                // Tornando alla ORM si torna a quella che c'era, intera: le separate restano in memoria.
                writeFinishSide(material, previous, undefined);
                writeFinishSide(material, next, back);
                delete remembered[next];
            }

            // IL CURSORE È UN GUADAGNO, non un valore assoluto: moltiplica la mappa, quindi a zero
            // la mappa non si vede e sembra rotta. Entrando in Separate la ruvidità parte da 1, che
            // è il valore neutro di una moltiplicazione (deciso in call, 2026-09-09).
            if (next === 'separate' && !material.roughness) material.roughness = 1;

            finishModeByMaterial.set(materialKey, next);
            setFinishMode(next);
            updateState(material, true);

            // IL COMMUTATORE È FATTO DI PULSANTI, e un pulsante non emette `change`: il pennarello
            // «da salvare» non lo sentiva. Ma cambiare flusso stacca delle mappe dal materiale, e
            // senza segnarlo «Save» rispondeva «nulla da salvare».
            onSceneChanged?.();
        },
        [finishMode, material, materialKey, updateState, onSceneChanged]
    );

    // Funzione per verificare se il materiale è Standard (non supportato)
    const isStandardMaterial = useCallback((material: any) => {
        // Un materiale Standard ha diffuseColor ma NON ha proprietà PBR avanzate
        return (
            material &&
            material.diffuseColor !== undefined &&
            (material.clearCoat === undefined || !material.clearCoat?.isEnabled) &&
            (material.metallic === undefined || material.metallic === 0) &&
            (material.roughness === undefined || material.roughness === 0) &&
            // Oppure verifica il tipo direttamente se disponibile
            (material instanceof BABYLON?.StandardMaterial || material.getClassName?.() === 'StandardMaterial')
        );
    }, []);

    if (!material || !helpers) {
        return (
            <InspectorShell kind='material' name={material?.name ?? ''}>
                <Spinner />
            </InspectorShell>
        );
    }

    const kindOfMaterial = material.getClassName?.() ?? T._('Material', 'Admin');

    // A QUALI OGGETTI È ADDOSSO. È la domanda che ci si fa guardando un materiale — «se lo cambio,
    // che cosa ridipingo?» — e la risposta la sa il motore: `getMeshesUsingMaterial` guarda anche
    // dentro i MultiMaterial, cioè le mesh che ne portano più d'uno e di cui questo è una parte.
    //
    // SI RICONTA A OGNI DISEGNO, e non è uno spreco: è un giro sulle mesh della scena. Una lista
    // tenuta da parte direbbe invece il conto di prima, perché l'assegnazione si cambia da un ALTRO
    // pannello — lo slot di una mesh — e da lì nessuno viene ad avvisare questo.
    //
    // ZERO OGGETTI, NIENTE PASTIGLIA: «0 objects» è la risposta a una domanda che nessuno ha fatto.
    // Contano gli oggetti veri: il motore elenca anche i nodi senza superficie che portano il materiale.
    const meshesUsingMaterial: any[] = (sceneEditor?.getMeshesUsingMaterial?.(material.id) ?? []).filter(hasSurface);

    const usedBy: InspectorUsage[] | undefined = meshesUsingMaterial.length
        ? [
              {
                  label: stringFormat(
                      meshesUsingMaterial.length === 1 ? T._('{0} object', 'Admin') : T._('{0} objects', 'Admin'),
                      meshesUsingMaterial.length.toString()
                  ),
                  current: meshesUsingMaterial.find((mesh) => sceneEditor?.isMeshSelected?.(mesh))?.id,
                  entries: meshesUsingMaterial.map((mesh): PropertiesPanelPickerOption => {
                      // I TRIANGOLI SONO UNA DIVISIONE, non un dato da chiedere a qualcuno: gli indici
                      // vanno a tre a tre. Ed è il dato che distingue due «Zone» che si chiamano quasi
                      // uguale — quale delle due è il pezzo grande.
                      const indices = typeof mesh.getTotalIndices === 'function' ? mesh.getTotalIndices() : 0;
                      const triangles = indices ? Math.round(indices / 3) : 0;

                      return {
                          value: mesh.id,
                          label: mesh.name,
                          caption: triangles ? `${triangles.toLocaleString()} ${T._('triangles', 'Admin')}` : undefined,
                          mark: <ObjectsIcon />
                      };
                  }),
                  // ANDARCI SONO DUE MOVIMENTI: la colonna di sinistra torna sugli oggetti e la riga
                  // giusta si apre. Li fa `revealTarget` — lo stesso giro della freccia «dove sta» —
                  // e qui si dice soltanto dove si vuole andare.
                  onPick: (value) => onOpenObject?.(String(value))
              }
          ]
        : undefined;

    // «APPLY TO SELECTION» (QA, 2026-10-12), come nel vecchio backoffice: il materiale come punto
    // d'ingresso, invece di passare da ogni oggetto e dal suo slot. La selezione è quella del palco —
    // il motore la tiene sulla mesh, e aprire un materiale non la toglie. Bastano le radici: un figlio
    // di una mesh scelta lo dipinge già lei, perché `applyMaterial` scende da sé nei figli. Le mesh
    // di riferimento del VTO non sono oggetti del prodotto, e non si dipingono.
    const readSelectedRoots = (): any[] =>
        (sceneEditor?.getSceneMeshes?.() ?? []).filter(
            (mesh: any) =>
                sceneEditor?.isMeshSelected?.(mesh) &&
                !(mesh.parent && sceneEditor.isMeshSelected(mesh.parent)) &&
                !String(mesh.name ?? '')
                    .toLowerCase()
                    .includes('vto_reference')
        );
    const selectedRoots = readSelectedRoots();

    // IN TESTATA, DOPO IL CONTO (QA, 2026-10-14, come nell'immagine del collega): «11 objects» e poi
    // «apply to selection» — a quanti è dato, e darlo anche alla scelta.
    const applyToSelection: InspectorMetaAction = {
        label: T._('Apply to selection', 'Admin'),
        disabled: !selectedRoots.length,
        title: selectedRoots.length ? undefined : T._('Select an object to apply this material', 'Admin'),
        onClick: () => {
            // LA SCELTA SI RILEGGE AL CLIC: la scheda la legge quando si disegna, e chi ha
            // deselezionato nel frattempo non deve vedersi dipingere quello che ha lasciato.
            //
            // E DICE COSA HA FATTO (round 4): la notifica, e l'elenco che rifà i suoi conti.
            const roots = readSelectedRoots();
            roots.forEach((mesh) => sceneEditor?.applyMaterial?.(material, mesh));
            updateState(material);
            redraw();
            notifyMaterialUsageChanged();
            onSceneChanged?.();
            showSuccessNotification({
                message:
                    roots.length === 1
                        ? stringFormat(T._('“{0}” applied to “{1}”', 'Admin'), material.name, roots[0].name)
                        : stringFormat(T._('“{0}” applied to {1} objects', 'Admin'), material.name, roots.length.toString())
            });
        }
    };

    // Se è un materiale Standard, mostra solo il messaggio di non supportato
    // La sfera fotografata è la stessa che la lista mostra nella tessera: qui si vede com'è venuta,
    // e premendola si riscatta dopo aver cambiato il materiale.
    const portrait = (
        <PreviewThumb
            type='button'
            $image={preview}
            $color={(material as any)?.albedoColor?.toHexString?.()}
            title={isCapturingPreview ? T._('Generating...', 'Admin') : T._('Regenerate preview', 'Admin')}
            aria-label={isCapturingPreview ? T._('Generating...', 'Admin') : T._('Regenerate preview', 'Admin')}
            data-busy={isCapturingPreview}
            onClick={capturePreview}
            disabled={isCapturingPreview || !sceneEditor}
        >
            <PreviewShot>
                <CamerasIcon />
            </PreviewShot>
        </PreviewThumb>
    );

    if (isStandardMaterial(material)) {
        return (
            <InspectorShell
                kind='material'
                name={material.name}
                specs={kindOfMaterial}
                usage={usedBy}
                metaAction={applyToSelection}
                onRename={handleMaterialNameChange}
                onReveal={onReveal}
                glyph={portrait}
            >
                <UnsupportedNote>{T._('Unsupported material', 'Admin')}</UnsupportedNote>
            </InspectorShell>
        );
    }

    // LE PROPRIETÀ SONO QUELLE DI SEMPRE, RIMESSE IN ORDINE. `buildPbrMaterialPanel` non le
    // costruisce: prende quelle che materialsPropertiesConfig costruisce già e le colloca nei
    // capitoli. Ciò che la tabella non nomina non compare — vedi pbrMaterialLayout.ts.
    const properties = materialsPropertiesCreator(material, helpers);

    // LA RIORGANIZZAZIONE È DEL SOLO `PBRMaterial` (utente, 2026-08-20). Gli altri due tipi PBR
    // hanno proprietà con altri nomi: passarli da questa tabella non li riorganizzerebbe, li
    // svuoterebbe — nessun id combacia, e resterebbe un pannello vuoto. «Ignorarli» vuol dire
    // lasciarli come sono, quindi si mostra l'elenco piatto di prima.
    const isClassicPbr = material instanceof BABYLON.PBRMaterial;
    const finish = finishRows(
        material,
        finishMode,
        () => {
            updateState(material);
            onSceneChanged?.();
        },
        helpers.getTexturePreviewUrl(material, 'metallic', false)
    );
    const sections = isClassicPbr
        ? buildPbrMaterialPanel(properties, [...buildPbrExtraProperties(material as BABYLON.PBRMaterial, helpers), ...finish.extra], {
              finish: finishMode,
              switcher: {
                  type: 'segmented',
                  id: 'finish-mode',
                  options: [
                      { value: 'orm', label: 'ORM' },
                      { value: 'separate', label: T._('Separate', 'Admin') }
                  ],
                  value: finishMode,
                  onChange: (value: string) => changeFinishMode(value as FinishMode),
                  ariaLabel: T._('Finish maps', 'Admin')
              },
              // LE RIGHE DEL PACCHETTO SI DISEGNANO SOLO SE UN PACCHETTO C'È: la mappa del solo metallo
              // occupa lo stesso campo (vedi `packSlotIsEmpty`), e mostrarla qui direbbe che il flusso
              // ORM è pieno mentre le mappe stanno nelle Separate. Resta la riga per caricarne una —
              // vuota — e spariscono miniatura, cestino, versione mobile e scalatura.
              //
              // LE LETTERE DEI CANALI arrivano da `finishRows`.
              //
              // E LE LETTERE DELL'INVERSIONE SONO PULSANTI anche loro: la spunta del generatore da cui
              // nascono non emette più `change`, e il «da salvare» si segna da qui.
              overrides: {
                  ...(finishMode === 'orm' && packSlotIsEmpty(material) ? hiddenPackRows() : {}),
                  ...finish.overrides,
                  ...Object.fromEntries(
                      ['invert-normal-map-X', 'invert-normal-map-Y'].map((id) => [
                          id,
                          (property: PropertiesPanelLeafConfig) =>
                              property.type === 'checkbox'
                                  ? {
                                        ...property,
                                        onChange: (checked: boolean) => {
                                            property.onChange?.(checked);
                                            onSceneChanged?.();
                                        }
                                    }
                                  : property
                      ])
                  )
              },
              // SCALATURA E CANALE UV DIVENTANO IL MAPPING DELLA TEXTURE — vedi `textureMapping`. Il menu
              // del canale UV non emette `change`, quindi il «da salvare» si segna da qui.
              mapping: (type) =>
                  textureMapping(material, type, () => {
                      updateState(material);
                      onSceneChanged?.();
                  })
          })
        : [];
    // MENTRE SI CERCA NON C'È PIÙ UN «ADVANCED»: una ricerca che risponde «niente» perché la risposta
    // stava nell'altra tab è una ricerca che mente. Chi cerca ha già detto cosa vuole vedere — si
    // mostra tutto quello che risponde, e alla parola cancellata il pannello torna com'era.
    const searching = parameterFilter.trim().length > 0;
    const found = searching ? filterPbrMaterialPanel(sections, parameterFilter) : sections;
    const plainSections = searching ? found : found.filter((section) => !section.advanced);
    const advancedSections = searching ? [] : found.filter((section) => section.advanced);

    return (
        // IL TIPO NON SI SCRIVE PER IL PBR (QA, 2026-10): i materiali sono uno solo, e «PBRMaterial» in
        // testata è un nome di classe che non distingue niente. Resta per gli altri tipi PBR, dove
        // spiega perché il pannello è l'elenco piatto, e per lo Standard, dove spiega il messaggio.
        <InspectorShell
            kind='material'
            name={material.name}
            specs={isClassicPbr ? undefined : kindOfMaterial}
            usage={usedBy}
            metaAction={applyToSelection}
            onRename={handleMaterialNameChange}
            onReveal={onReveal}
            glyph={portrait}
        >
            {/* IN EVIDENZA RESTA LA SOLA SURFACE — colore, finitura, trasparenza: ciò che si
                tocca a ogni materiale. Gli altri capitoli entrano interi sotto la spina, e il
                pannello parte con tre gruppi invece che con diciotto. */}
            {!isClassicPbr && <PropertiesPanel properties={properties} />}

            {/* Ogni riga del materiale ha la colonna del lucchetto: vedi `PropertiesPanelBullets`. */}
            <PropertiesPanelBulletsProvider value='dot'>
                {/* Un materiale che non è un PBR classico ha un pannello solo, senza capitoli né tab. */}
                {!isClassicPbr && <PropertiesPanel properties={properties} />}

                {/* CERCARE NON È MODIFICARE: `data-view-only` dice a chi ascolta le modifiche, sopra
                il palco, che da qui non esce niente da salvare. */}
                {isClassicPbr && (
                    <ParameterSearch data-view-only='true'>
                        <SearchInput
                            placeholder={T._('Search parameters...', 'Admin')}
                            aria-label={T._('Search parameters', 'Admin')}
                            value={parameterFilter}
                            onChange={(event) => setParameterFilter(event.currentTarget.value)}
                        />
                    </ParameterSearch>
                )}

                {/* MENTRE SI CERCA LE TAB SI FANNO DA PARTE: la ricerca guarda in tutte, e ciò che risponde si
                mostra intero. IL PANNELLO SI RIMONTA A OGNI PAROLA CERCATA, ed è voluto: quello che
                risponde deve aprirsi, e una card decide se nascere aperta solo quando nasce. */}
                {searching && titledChapters(found, parameterFilter)}

                {searching && found.length === 0 && <NoMatch>{T._('No parameter with this name', 'Admin')}</NoMatch>}

                {/* SURFACE E ADVANCED SONO DUE TAB (round 4, pacchetto C), le stesse del Composer: in
                Surface ciò che si tocca a ogni materiale — colore, finitura, trasparenza —, in
                Advanced gli altri capitoli interi. Prima Advanced era una spina in fondo alla colonna. */}
                {!searching &&
                    (advancedSections.length > 0 ? (
                        <PropertiesPanelTabs
                            memoryKey='material'
                            tabs={[
                                { id: 'surface', label: T._('Surface', 'Admin'), content: chapters(plainSections) },
                                { id: 'advanced', label: T._('Advanced', 'Admin'), content: chapters(advancedSections) }
                            ]}
                        />
                    ) : (
                        chapters(plainSections)
                    ))}
            </PropertiesPanelBulletsProvider>
        </InspectorShell>
    );
};

export default MaterialOverlaySimple;
